import { useEffect, useRef, useState } from "react";
import type { PublicCallConfig } from "../scenario/types";
import { audio } from "../lib/audio";

function fmt(s: number) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

type Phase = "ringing" | "connecting" | "talking" | "deciding" | "closing" | "ended";
interface Line {
  id: string;
  who: "caller" | "you";
  text: string;
}

/**
 * An inbound colleague phone call while Max is in public (on the train). Nina asks
 * for confidential deal details. Rendered as a real ringing phone (ItCall shell):
 * ring -> Answer -> the colleague speaks (TTS + avatar card + live conversation panel) ->
 * the learner picks a reply ON the call. Unlike the IT-support call this BRANCHES:
 * blurting details / speakerphone = risky; call back privately / headset + general
 * = safe. Nina's scripted reply plays, then the outcome is recorded.
 */
export function ColleagueCall({
  config,
  onChoose,
}: {
  config: PublicCallConfig;
  onChoose: (opt: PublicCallConfig["options"][number]) => void;
}) {
  const [phase, setPhase] = useState<Phase>("ringing");
  const [seconds, setSeconds] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const [lines, setLines] = useState<Line[]>([]);
  const mounted = useRef(true);
  const doneRef = useRef(false);
  const convoRef = useRef<HTMLDivElement>(null);

  const lang = config.language || "en-US";
  const voice = config.voiceId === "onyx" ? "nova" : config.voiceId || "nova";
  const tone =
    "Speak as a friendly, busy female sales-ops colleague on the phone: warm, quick, matter-of-fact.";
  const initials = config.callerName.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  useEffect(() => {
    audio.startRing();
    const opts = { lang, voice, instructions: tone };
    void audio.prefetchSpeech(config.opener, opts);
    for (const o of config.options) if (o.replyLine) void audio.prefetchSpeech(o.replyLine, opts);
    return () => {
      mounted.current = false;
      audio.stopRing();
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase === "ringing" || phase === "ended") return;
    const t = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(t);
  }, [phase]);

  useEffect(() => {
    convoRef.current?.scrollTo({ top: convoRef.current.scrollHeight, behavior: "smooth" });
  }, [lines]);

  const wait = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

  const say = async (who: "caller", text: string) => {
    if (!mounted.current) return;
    setSpeaking(true);
    await audio.speak(text, {
      lang,
      voice,
      instructions: tone,
      onStart: () => {
        if (mounted.current) setLines((l) => [...l, { id: `c_${l.length}`, who, text }]);
      },
    });
    if (!audio.isEnabled()) await wait(Math.min(6000, 1400 + text.length * 34));
    if (mounted.current) setSpeaking(false);
  };

  const answer = async () => {
    audio.stopRing();
    setPhase("connecting");
    await wait(650);
    if (!mounted.current) return;
    setPhase("talking");
    await say("caller", config.opener);
    if (!mounted.current) return;
    setPhase("deciding");
  };

  const choose = async (opt: PublicCallConfig["options"][number]) => {
    if (doneRef.current) return;
    doneRef.current = true;
    setLines((l) => [...l, { id: `u_${l.length}`, who: "you", text: opt.label }]);
    setPhase("closing");
    if (opt.replyLine) await say("caller", opt.replyLine);
    await wait(350);
    audio.stopRing();
    audio.cancelSpeech();
    setPhase("ended");
    window.setTimeout(() => onChoose(opt), 350);
  };

  const ringing = phase === "ringing";
  const connecting = phase === "connecting";
  const connected = phase === "talking" || phase === "deciding" || phase === "closing";

  return (
    <div className="itcall itc-ios">
      <div className="itc-convo" ref={convoRef}>
        <div className="itc-convo-head">
          <span className="itc-convo-bars"><i /> <i /> <i /></span>
          Call · {config.callerName}
        </div>
        {lines.map((l) => (
          <div key={l.id} className={`itc-line ${l.who}`}>
            <div className="itc-who">{l.who === "caller" ? config.callerName : "You"}</div>
            {l.text}
          </div>
        ))}
        {speaking && <div className="itc-typing"><i /><i /><i /></div>}
      </div>

      {/* The phone, drawn as an iPhone (same look as the ransomware IT call):
          shakes while ringing, live voice meter while Nina speaks. */}
      <div className={`itc-phone ${ringing ? "is-ringing" : ""}`}>
        <div className="itc-island" aria-hidden />
        <div className="itc-status" aria-hidden>
          <span>{"14:26"}</span>
          <span className="itc-status-r">
            <i className="sig" />
            <i className="bat" />
          </span>
        </div>
        <div className="itc-screen">
          <div className="itc-label">
            {ringing ? "Incoming call" : connecting ? "Connecting\u2026" : connected ? "On the call" : "Call ended"}
          </div>
          <div className={`itc-avatar ${speaking ? "speaking" : ""}`}>
            <span className="itc-initials" aria-hidden>{initials || "\u{1F4DE}"}</span>
            {ringing && (
              <>
                <span className="itc-ring-1" />
                <span className="itc-ring-2" />
              </>
            )}
          </div>
          <div className="itc-caller">
            <strong>{config.callerName}</strong>
            <span>{ringing ? "incoming call\u2026" : config.callerSubtitle || "mobile"}</span>
          </div>
          <div className="itc-time">{connected ? fmt(seconds) : ""}</div>
          {(connected || connecting) && (
            <div className={`itc-meter ${speaking ? "on" : ""}`} role="img" aria-label={speaking ? `${config.callerName} is speaking` : "Nobody is speaking"}>
              {Array.from({ length: 15 }).map((_, i) => (
                <i key={i} style={{ animationDelay: `${(i * 137) % 520}ms` }} />
              ))}
            </div>
          )}
        </div>

        <div className="itc-controls">
          {ringing && (
            <button className="itc-btn answer" onClick={answer}>
              <span className="itc-btn-ico" aria-hidden>{"\u{1F4DE}"}</span>
              <span className="itc-btn-txt">Answer</span>
            </button>
          )}

          {connecting && <div className="itc-banner">{"Connecting\u2026"}</div>}

          {phase === "deciding" && (
            <div className="itc-decide">
              <p className="itc-q">How should Max reply?</p>
              {config.options.map((o) => (
                <button key={o.id} className="itc-choice" onClick={() => choose(o)}>
                  {o.label}
                </button>
              ))}
            </div>
          )}

          {(phase === "talking" || phase === "closing") && (
            <div className="itc-onair">{speaking ? `${config.callerName} is speaking\u2026` : "On the call"}</div>
          )}
        </div>
        <div className="itc-homebar" aria-hidden />
      </div>
    </div>
  );
}
